import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Eye, EyeOff, AlertCircle, RefreshCw, Phone, MessageCircle, FileText } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useTenant } from '@/hooks/useTenant';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { SOFTPHONE_OPT_OUT_KEY } from '@/components/phone/SoftphoneProvider';
import { APP_CONFIG } from '@/config/app';

/** Settings body for the CallTrackingMetrics integration (credentials, softphone, health, history sync). */
export function CtmIntegrationPanel() {
  const { data: company, update, isUpdating } = useCompanyProfile();
  const { companyQueryKey } = useTenant();
  const queryClient = useQueryClient();

  // CTM Integration (Account ID = Master Key)
  const [ctmAccountId, setCtmAccountId] = useState('');
  const [ctmAccessKey, setCtmAccessKey] = useState('');
  const [ctmSecretKey, setCtmSecretKey] = useState('');
  const [ctmShowSecrets, setCtmShowSecrets] = useState(false);
  const [ctmValidating, setCtmValidating] = useState(false);
  const [integrationHealth, setIntegrationHealth] = useState<{ score: number; criticalFailures: string[]; attributionWarnings: string[] } | null>(null);

  // CTM Sync state
  const defaultEnd = new Date().toISOString().slice(0, 10);
  const defaultStart = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [syncStartDate, setSyncStartDate] = useState(defaultStart);
  const [syncEndDate, setSyncEndDate] = useState(defaultEnd);
  const [syncCalls, setSyncCalls] = useState(true);
  const [syncSms, setSyncSms] = useState(true);
  const [syncForms, setSyncForms] = useState(false);
  const [leadNewCallers, setLeadNewCallers] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ calls_synced: number; sms_synced: number; forms_synced?: number; total_errors: number; errors: string[] } | null>(null);
  const [softphoneDeviceOptOut, setSoftphoneDeviceOptOut] = useState(
    () => localStorage.getItem(SOFTPHONE_OPT_OUT_KEY) === 'true'
  );

  useEffect(() => {
    if (company) {
      setCtmAccountId(company.ctm_account_id || '');
    }
  }, [company]);

  // CTM: Credential validation then save to company (Account ID = Master Key)
  const handleSaveCTMConfig = async () => {
    if (!ctmAccountId?.trim()) {
      toast({ title: 'Account ID is required', variant: 'destructive' });
      return;
    }
    const updatingKeys = ctmAccessKey.trim() || ctmSecretKey.trim();
    if (updatingKeys && (!ctmAccessKey?.trim() || !ctmSecretKey?.trim())) {
      toast({ title: 'Provide both Access Key and Secret Key to update credentials', variant: 'destructive' });
      return;
    }
    if (updatingKeys) {
      setCtmValidating(true);
      try {
        const { data: validateResult, error: validateError } = await supabase.functions.invoke('validate-ctm-credentials', {
          body: { accountId: ctmAccountId.trim(), accessKey: ctmAccessKey.trim(), secretKey: ctmSecretKey.trim() },
        });
        if (validateError) {
          toast({ title: 'Validation failed', description: validateError.message, variant: 'destructive' });
          return;
        }
        if (!validateResult?.valid) {
          toast({ title: 'Invalid CTM credentials', description: validateResult?.error || 'Please check Account ID and API keys.', variant: 'destructive' });
          return;
        }
        const { error: saveError } = await supabase.rpc('set_ctm_credentials', {
          p_account_id: ctmAccountId.trim(),
          p_access_key: ctmAccessKey.trim(),
          p_secret_key: ctmSecretKey.trim(),
        });
        if (saveError) {
          toast({ title: 'Could not save CTM credentials', description: saveError.message, variant: 'destructive' });
          return;
        }
        await queryClient.invalidateQueries({ queryKey: companyQueryKey });
        toast({ title: 'CTM credentials saved' });
        setCtmAccessKey('');
        setCtmSecretKey('');
        setIntegrationHealth(null);
      } finally {
        setCtmValidating(false);
      }
    } else {
      update({ ctm_account_id: ctmAccountId.trim() });
      setIntegrationHealth(null);
    }
  };

  const fetchIntegrationHealth = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('integration-health');
      if (!error && data) setIntegrationHealth(data);
    } catch {
      setIntegrationHealth(null);
    }
  };

  const handleSyncCTM = async () => {
    if (!syncCalls && !syncSms && !syncForms) {
      toast({ title: 'Select at least one data type to sync', variant: 'destructive' });
      return;
    }
    setIsSyncing(true);
    setSyncResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('ctm-sync-history', {
        body: {
          company_id: company?.id,
          start_date: syncStartDate,
          end_date: syncEndDate,
          sync_calls: syncCalls,
          sync_sms: syncSms,
          sync_forms: syncForms,
          lead_new_callers: syncCalls && leadNewCallers,
        },
      });
      if (error) throw new Error(error.message);
      setSyncResult(data);
      toast({
        title: 'CTM sync complete',
        description: `${data.calls_synced} calls · ${data.sms_synced} SMS · ${data.forms_synced ?? 0} forms synced${data.leads_created ? ` · ${data.leads_created} new leads` : ''}${data.total_errors > 0 ? ` · ${data.total_errors} errors` : ''}`,
      });
    } catch (e) {
      toast({
        title: 'Sync failed',
        description: e instanceof Error ? e.message.slice(0, 300) : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const hasCTMCredentials = Boolean(company?.ctm_account_id && company?.ctm_credentials_set_at);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Connect your CTM account for call tracking, lead sync, and revenue attribution. Account ID is the Master Key. All data is routed by this ID.
      </p>

      <section className="space-y-4">
        <h4 className="font-medium">Credentials</h4>
        <div className="space-y-2">
          <Label htmlFor="ctm-account-id">CTM Account ID *</Label>
          <Input
            id="ctm-account-id"
            value={ctmAccountId}
            onChange={(e) => setCtmAccountId(e.target.value)}
            placeholder="e.g. 502"
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            Primary partition key. If missing or wrong, no data moves. Find this in your CTM account settings.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ctm-access-key">Access Key</Label>
          <Input
            id="ctm-access-key"
            type={ctmShowSecrets ? 'text' : 'password'}
            value={ctmAccessKey}
            onChange={(e) => setCtmAccessKey(e.target.value)}
            placeholder={hasCTMCredentials ? 'Leave blank to keep existing' : 'Required'}
            className="font-mono"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ctm-secret-key">Secret Key</Label>
          <div className="flex gap-2">
            <Input
              id="ctm-secret-key"
              type={ctmShowSecrets ? 'text' : 'password'}
              value={ctmSecretKey}
              onChange={(e) => setCtmSecretKey(e.target.value)}
              placeholder={hasCTMCredentials ? 'Leave blank to keep existing' : 'Required'}
              className="font-mono"
            />
            <Button variant="outline" size="icon" onClick={() => setCtmShowSecrets(!ctmShowSecrets)} title={ctmShowSecrets ? 'Hide' : 'Show'}>
              {ctmShowSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <Button onClick={handleSaveCTMConfig} disabled={isUpdating || ctmValidating} className="w-full sm:w-auto">
          {ctmValidating ? 'Verifying...' : isUpdating ? 'Saving...' : 'Validate & Save CTM Credentials'}
        </Button>
      </section>

      {hasCTMCredentials && (
        <section className="border-t pt-4 space-y-4">
          <h4 className="font-medium flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Embedded Softphone
          </h4>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="softphone-enabled">Enable embedded softphone</Label>
              <p className="text-xs text-muted-foreground">
                CSRs answer and place calls inside the CRM with live caller identification.
                {company?.ctm_mode === 'agency'
                  ? ' A CTM agent login is created automatically for every administrator and CSR; each person’s status is on Settings → Users.'
                  : ' Each CSR needs a CTM user license with a matching email address.'}
              </p>
            </div>
            <Switch
              id="softphone-enabled"
              checked={Boolean(company?.softphone_enabled)}
              onCheckedChange={(v) => update({ softphone_enabled: v })}
              disabled={isUpdating}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="softphone-device-opt-out">Disable on this device</Label>
              <p className="text-xs text-muted-foreground">
                Hides the softphone in this browser only. Takes effect on next page load.
              </p>
            </div>
            <Switch
              id="softphone-device-opt-out"
              checked={softphoneDeviceOptOut}
              onCheckedChange={(v) => {
                setSoftphoneDeviceOptOut(v);
                if (v) localStorage.setItem(SOFTPHONE_OPT_OUT_KEY, 'true');
                else localStorage.removeItem(SOFTPHONE_OPT_OUT_KEY);
              }}
            />
          </div>
        </section>
      )}

      {hasCTMCredentials && (
        <section className="border-t pt-4 space-y-3">
          <h4 className="font-medium flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Integration Health{integrationHealth !== null && `: ${integrationHealth.score}/100`}
          </h4>
          {integrationHealth !== null && integrationHealth.criticalFailures?.length > 0 && (
            <ul className="text-sm text-destructive space-y-1">
              {integrationHealth.criticalFailures.map((msg, i) => (
                <li key={i}>Critical: {msg}</li>
              ))}
            </ul>
          )}
          {integrationHealth !== null && integrationHealth.attributionWarnings?.length > 0 && (
            <ul className="text-sm text-warning space-y-1">
              {integrationHealth.attributionWarnings.map((msg, i) => (
                <li key={i}>Warning: {msg}</li>
              ))}
            </ul>
          )}
          <Button variant="outline" size="sm" onClick={fetchIntegrationHealth}>
            {integrationHealth !== null ? 'Refresh health' : 'Check Integration Health'}
          </Button>
        </section>
      )}

      {hasCTMCredentials && (
        <section className="border-t pt-4 space-y-4">
          <h4 className="font-medium flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Sync Historical Data
          </h4>
          <p className="text-sm text-muted-foreground">
            Pull past calls, SMS and form submissions from CTM into {APP_CONFIG.name}. Duplicates are skipped automatically. Forms create leads dated at submission time, so tick it deliberately.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Start date</label>
              <input
                type="date"
                value={syncStartDate}
                onChange={(e) => setSyncStartDate(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">End date</label>
              <input
                type="date"
                value={syncEndDate}
                onChange={(e) => setSyncEndDate(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={syncCalls} onChange={(e) => setSyncCalls(e.target.checked)} className="rounded" />
              <Phone className="h-3.5 w-3.5" />
              Calls
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={syncSms} onChange={(e) => setSyncSms(e.target.checked)} className="rounded" />
              <MessageCircle className="h-3.5 w-3.5" />
              SMS
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={syncForms} onChange={(e) => setSyncForms(e.target.checked)} className="rounded" />
              <FileText className="h-3.5 w-3.5" />
              Forms (creates leads)
            </label>
            <label className={`flex items-center gap-2 text-sm cursor-pointer ${syncCalls ? '' : 'opacity-50'}`}>
              <input
                type="checkbox"
                checked={leadNewCallers}
                disabled={!syncCalls}
                onChange={(e) => setLeadNewCallers(e.target.checked)}
                className="rounded"
              />
              <Phone className="h-3.5 w-3.5" />
              First-time callers become leads
            </label>
          </div>
          <Button onClick={handleSyncCTM} disabled={isSyncing} className="w-full sm:w-auto">
            {isSyncing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync Now
              </>
            )}
          </Button>
          {syncResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg border p-3 text-center">
                  <div className="text-2xl font-bold">{syncResult.calls_synced}</div>
                  <div className="text-xs text-muted-foreground mt-1">Calls synced</div>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <div className="text-2xl font-bold">{syncResult.sms_synced}</div>
                  <div className="text-xs text-muted-foreground mt-1">SMS synced</div>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <div className="text-2xl font-bold">{syncResult.forms_synced ?? 0}</div>
                  <div className="text-xs text-muted-foreground mt-1">Forms synced</div>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <div className={`text-2xl font-bold ${syncResult.total_errors > 0 ? 'text-destructive' : ''}`}>
                    {syncResult.total_errors}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Errors</div>
                </div>
              </div>
              {syncResult.errors?.length > 0 && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-1">
                  {syncResult.errors.map((err, i) => (
                    <p key={i} className="text-xs text-destructive font-mono">{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
